import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * ПОЛЕ ОТВЕТА, КОТОРОЕ РАСТЁТ ВМЕСТЕ С ТЕКСТОМ (`s713`, Д-09 приёмки 05.10.2026).
 *
 * Слова задания: «поле ввода растёт от одной до примерно пяти строк при переносах,
 * длинном тексте и вставке; дальше внутренний скролл. Уменьшается после удаления,
 * сохраняет черновик и видимость курсора, не закрывает кнопки. Не менять молча
 * поведение Enter».
 *
 * Было однострочное поле: длинный ответ уезжал за край, вставленный абзац терял
 * переносы. Стало многострочное: одна строка в покое (вид прежний — «как в тг»,
 * владелец 18.09.2026, `s608`), растёт до пяти строк, дальше крутится внутри себя.
 *
 * ⛔ ENTER — КАК БЫЛО: отправляет. Перенос строки — Shift+Enter (раньше он не делал
 * ничего). Набор через системный ввод (IME) Enter не отправляет.
 *
 * ⛔ КНОПКИ ЛЕЖАТ ПОВЕРХ ПОЛЯ И ПРИЖАТЫ К НИЗУ — их ставит родитель; поле лишь
 * держит под них отбивку слева и справа, поэтому текст под кнопки не заходит.
 */
const STROK = 5;

export function PoleOtveta({
  tekst,
  onTekst,
  onOtpravit,
  placeholder,
  onNachalo,
  tolkoChtenie = false,
  enterPerenosit = false,
}: {
  tekst: string;
  onTekst: (tekst: string) => void;
  onOtpravit: () => void;
  placeholder: string;
  /** Человек начал редактировать (курсор в поле) — экран берёт блокировку редактирования. */
  onNachalo?: () => void;
  /** Редактирует другой: черновик виден, но не правится (сервер всё равно не пустит ход). */
  tolkoChtenie?: boolean;
  /**
   * `s720`, быстрый ответ из уведомления: Enter — перенос строки, отправляет Ctrl+Enter (на Mac —
   * ⌘+Enter) или кнопка. Слово задания; в обычной переписке Enter отправляет, как раньше.
   */
  enterPerenosit?: boolean;
}) {
  const pole = useRef<HTMLTextAreaElement | null>(null);

  const podognat = useCallback(() => {
    const el = pole.current;
    if (el === null) return;

    const stil = getComputedStyle(el);
    const stroka = parseFloat(stil.lineHeight) || parseFloat(stil.fontSize) * 1.4;
    const polya = parseFloat(stil.paddingTop) + parseFloat(stil.paddingBottom);
    const ramka = parseFloat(stil.borderTopWidth) + parseFloat(stil.borderBottomWidth);
    const potolok = Math.round(stroka * STROK + polya + ramka);

    // Сначала «по содержимому», иначе поле не уменьшится после удаления.
    el.style.height = "auto";
    const nuzhno = el.scrollHeight + ramka;
    el.style.height = `${Math.min(nuzhno, potolok)}px`;
    el.style.overflowY = nuzhno > potolok ? "auto" : "hidden";
  }, []);

  // Высота — по тексту: набор, вставка, диктовка, подстановка шаблона, смена беседы.
  useLayoutEffect(podognat, [tekst, podognat]);

  // Ширина поля изменилась (окно, колонка клиентки) — переносы другие, высота тоже.
  useEffect(() => {
    const el = pole.current;
    if (el === null || typeof ResizeObserver === "undefined") return;

    let shirina = el.clientWidth;
    const nablyudatel = new ResizeObserver(() => {
      if (el.clientWidth !== shirina) {
        shirina = el.clientWidth;
        podognat();
      }
    });
    nablyudatel.observe(el);

    return () => nablyudatel.disconnect();
  }, [podognat]);

  return (
    <textarea
      ref={pole}
      rows={1}
      value={tekst}
      data-testid="pole-otveta"
      data-enter={enterPerenosit ? "perenos" : "otpravka"}
      readOnly={tolkoChtenie}
      onFocus={onNachalo}
      onChange={(e) => onTekst(e.target.value)}
      onKeyDown={(e) => {
        if (e.key !== "Enter" || e.nativeEvent.isComposing) return;

        if (enterPerenosit) {
          // Перенос делает само поле; отправка — только сочетанием.
          if (e.ctrlKey || e.metaKey) {
            e.preventDefault();
            if (!tolkoChtenie) onOtpravit();
          }

          return;
        }

        if (!e.shiftKey) {
          e.preventDefault();
          if (!tolkoChtenie) onOtpravit();
        }
      }}
      placeholder={placeholder}
      className="block min-w-0 flex-1 resize-none rounded-[22px] border border-ops-border bg-ops-surface py-2.5 pl-12 pr-12 text-ops-body text-ops-text outline-none focus:border-ops-primary"
    />
  );
}
